import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  SunIcon,
  MoonIcon,
  Bars3Icon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { useDarkMode } from "../hooks/useDarkMode";
import { cn } from "../utils/cn";
import { useSettings } from "../contexts/SettingsContext";

const navigation = [
  { name: "Home", href: "#home" },
  { name: "Features", href: "#features" },
  { name: "How It Works", href: "#how-it-works" },
  { name: "Partner with us", href: "#partners" },
  { name: "Institutions", href: "#institutions" },
  { name: "Docs", href: "https://de5.gitbook.io/de5-docs/" },
  { name: "Blog", href: "#blog" },
];

export default function Header() {
  const { settings } = useSettings();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isDarkMode, toggleDarkMode } = useDarkMode();

  return (
    <motion.header
      className="fixed left-0 right-0 top-0 z-50 bg-white/90 dark:bg-white/10 backdrop-blur-md border-b border-gray-200/50 dark:border-white/20"
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
    >
      <nav className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-4 max-w-7xl mx-auto">
        {/* Logo */}
        <motion.div
          className="flex lg:flex-1"
          whileHover={{ scale: 1.05 }}
          transition={{ type: "spring", stiffness: 400, damping: 17 }}
        >
          <a href="#home" className="-m-1.5 p-1.5">
            <span className="sr-only">de5.ai</span>
            <h1
              className="text-2xl sm:text-3xl lg:text-4xl font-oxanium font-bold"
              style={{ color: settings.logoColor }}
            >
              de5.ai
            </h1>
          </a>
        </motion.div>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex lg:gap-x-8">
          {navigation.map((item, index) => (
            <motion.a
              key={item.name}
              href={item.href}
              target={item.href.startsWith("http") ? "_blank" : undefined}
              rel={
                item.href.startsWith("http") ? "noopener noreferrer" : undefined
              }
              className="text-sm font-semibold leading-6 text-gray-700 dark:text-gray-300 transition-colors"
              style={
                {
                  "--hover-color": settings.primaryColor,
                } as React.CSSProperties
              }
              onMouseEnter={(e) => {
                e.currentTarget.style.color = settings.primaryColor;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = "";
              }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
              whileHover={{ scale: 1.05 }}
            >
              {item.name}
            </motion.a>
          ))}
        </div>

        {/* Right side controls */}
        <div className="hidden lg:flex lg:flex-1 lg:justify-end lg:items-center lg:gap-4">
          {/* Dark mode toggle */}
          <motion.button
            onClick={toggleDarkMode}
            className="p-2 rounded-lg glass-effect hover:bg-white/20 dark:hover:bg-white/10 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
          >
            <AnimatePresence mode="wait">
              {isDarkMode ? (
                <motion.div
                  key="sun"
                  initial={{ rotate: -90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: 90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <SunIcon className="h-5 w-5 text-white" />
                </motion.div>
              ) : (
                <motion.div
                  key="moon"
                  initial={{ rotate: 90, opacity: 0 }}
                  animate={{ rotate: 0, opacity: 1 }}
                  exit={{ rotate: -90, opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <MoonIcon className="h-5 w-5 text-black" />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>

          {/* Get in touch CTA */}
          <motion.button
            onClick={() => {
              const ctaSection = document.getElementById("cta");
              if (ctaSection) {
                ctaSection.scrollIntoView({ behavior: "smooth" });
              }
            }}
            className={cn(
              "flex items-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm transition-all duration-300",
              "text-white hover:shadow-lg",
              "glow-button"
            )}
            style={{
              backgroundColor: settings.headerButtonColor,
              boxShadow: `0 4px 14px 0 ${settings.headerButtonColor}40`,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor =
                settings.headerButtonColor;
              e.currentTarget.style.boxShadow = `0 6px 20px 0 ${settings.headerButtonColor}66`;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor =
                settings.headerButtonColor;
              e.currentTarget.style.boxShadow = `0 4px 14px 0 ${settings.headerButtonColor}40`;
            }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <span className="text-white">Get in touch</span>
          </motion.button>
        </div>

        {/* Mobile menu button */}
        <div className="flex lg:hidden">
          <button
            type="button"
            className="-m-2.5 inline-flex items-center justify-center rounded-md p-2.5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={() => setMobileMenuOpen(true)}
          >
            <span className="sr-only">Open main menu</span>
            <Bars3Icon className="h-6 w-6" aria-hidden="true" />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            className="lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm" />
            <motion.div
              className="fixed inset-0 z-[60] w-full h-screen overflow-y-auto overflow-x-hidden bg-white/95 dark:bg-gray-900/95 backdrop-blur-md px-6 py-6"
              initial={{ y: "-100%" }}
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              transition={{ type: "spring", damping: 25, stiffness: 200 }}
            >
              <div className="flex items-center justify-between">
                <a href="#home" className="-m-1.5 p-1.5">
                  <span
                    className="text-2xl sm:text-3xl lg:text-4xl font-oxanium font-bold"
                    style={{ color: settings.logoColor }}
                  >
                    de5.ai
                  </span>
                </a>
                <button
                  type="button"
                  className="-m-2.5 rounded-md p-2.5 text-gray-700 dark:text-gray-300"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div className="mt-6 flow-root">
                <div className="-my-6 divide-y divide-gray-500/10">
                  <div className="space-y-2 py-6">
                    {navigation.map((item) => (
                      <a
                        key={item.name}
                        href={item.href}
                        target={
                          item.href.startsWith("http") ? "_blank" : undefined
                        }
                        rel={
                          item.href.startsWith("http")
                            ? "noopener noreferrer"
                            : undefined
                        }
                        className="-mx-3 block rounded-lg px-3 py-2 text-base font-semibold leading-7 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-800"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {item.name}
                      </a>
                    ))}
                  </div>
                  <div className="py-6 space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        Dark Mode
                      </span>
                      <button
                        onClick={toggleDarkMode}
                        className="p-2 rounded-lg glass-effect"
                      >
                        {isDarkMode ? (
                          <SunIcon className="h-5 w-5 text-white" />
                        ) : (
                          <MoonIcon className="h-5 w-5 text-black" />
                        )}
                      </button>
                    </div>
                    <button
                      onClick={() => {
                        const ctaSection = document.getElementById("cta");
                        if (ctaSection) {
                          ctaSection.scrollIntoView({ behavior: "smooth" });
                        }
                        setMobileMenuOpen(false);
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg font-semibold text-sm text-white"
                      style={{ backgroundColor: settings.headerButtonColor }}
                    >
                      <span className="text-white">Get in touch</span>
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
